/**
 * Application-level error boundary.
 *
 * Without this, any component that throws while rendering takes the
 * entire page white (or — in dev — drops into Vite's bright-red
 * unstyled overlay). This catches the throw, logs it, and renders a
 * branded recovery UI with copy-error / retry / home actions so the
 * user is never stuck staring at a dev-tools dump.
 *
 * Class component — React error boundaries cannot be expressed as hooks.
 */
import { AlertOctagon, Check, Copy, Home, LifeBuoy, RotateCw } from 'lucide-react';
import React from 'react';

import Link from '@/components/ui/link';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  // eslint-disable-next-line react/require-default-props -- runtime defaultProps set below the class
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage?: string;
  errorStack?: string;
  componentStack?: string;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    if (error instanceof Error) {
      return {
        hasError: true,
        errorMessage: error.message,
        errorStack: error.stack,
      };
    }
    return {
      hasError: true,
      errorMessage: 'Something went wrong rendering this page.',
    };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Render error:', error, info);
    this.setState({ componentStack: info.componentStack ?? undefined });
  }

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      errorMessage: undefined,
      errorStack: undefined,
      componentStack: undefined,
    });
  };

  render(): React.ReactNode {
    const { hasError, errorMessage, errorStack, componentStack } = this.state;
    const { children, fallback } = this.props;
    if (!hasError) return children;
    if (fallback) return fallback;

    return (
      <ErrorFallback
        message={errorMessage}
        stack={errorStack}
        componentStack={componentStack}
        onReset={this.handleReset}
      />
    );
  }
}

interface ErrorFallbackProps {
  message?: string;
  stack?: string;
  componentStack?: string;
  onReset: () => void;
}

function buildClipboardPayload(props: ErrorFallbackProps): string {
  const lines = [
    `Error: ${props.message || 'Unknown error'}`,
    '',
    `URL: ${typeof window === 'undefined' ? '' : window.location.href}`,
    `User-Agent: ${
      typeof navigator === 'undefined' ? '' : navigator.userAgent
    }`,
    `Time: ${new Date().toISOString()}`,
    '',
  ];
  if (props.stack) {
    lines.push('--- Stack ---', props.stack, '');
  }
  if (props.componentStack) {
    lines.push('--- Component stack ---', props.componentStack.trim(), '');
  }
  return lines.join('\n');
}

// eslint-disable-next-line max-lines-per-function -- single coherent recovery surface
function ErrorFallback(props: ErrorFallbackProps): React.ReactElement {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (): Promise<void> => {
    const payload = buildClipboardPayload(props);
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(payload);
      } else if (typeof document !== 'undefined') {
        // Fallback for very old browsers / non-secure contexts.
        const textarea = document.createElement('textarea');
        textarea.value = payload;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.append(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write blocked — leave the button alone so the user
      // can long-press the pre block to copy manually.
    }
  };

  return (
    <div className="relative flex min-h-[80vh] flex-col items-center justify-center px-4 py-12">
      {/* Ambient gradient — kept subtle so the focus stays on the card. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-destructive/10 via-transparent to-transparent"
      />

      <div className="relative w-full max-w-xl rounded-2xl border border-border/60 bg-card/80 p-6 shadow-xl backdrop-blur sm:p-8">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
            <AlertOctagon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold tracking-tight text-foreground">
              Something broke
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              An unexpected error happened while rendering this page. Try
              again, or head home and we'll keep things running.
            </p>
          </div>
        </div>

        {props.message ? (
          <details className="mt-5 overflow-hidden rounded-xl border border-border/60 bg-background/60">
            <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
              <span className="truncate">{props.message}</span>
              <span className="flex-shrink-0 text-[10px] uppercase tracking-widest text-muted-foreground/70">
                details
              </span>
            </summary>
            <pre className="max-h-64 overflow-auto border-t border-border/60 bg-background/80 px-3 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {props.stack || props.message}
              {props.componentStack ? `\n\n${props.componentStack.trim()}` : ''}
            </pre>
          </details>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={props.onReset}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Try again
          </button>
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-4 py-2 text-xs font-semibold text-foreground transition-colors hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <Home className="h-3.5 w-3.5" />
            Go home
          </Link>
          <button
            type="button"
            onClick={handleCopy}
            aria-live="polite"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-4 py-2 text-xs font-semibold text-foreground transition-colors hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy error
              </>
            )}
          </button>
        </div>
      </div>

      {/* Bottom report banner — anchored to the viewport so it's visible
        * even on tall stack traces. Externally linked, so we use a raw
        * `<a>` rather than the SPA Link helper. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        <a
          href="https://1ani.me/help-center"
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto inline-flex max-w-full items-center gap-2 rounded-full border border-border/70 bg-card/95 px-4 py-2 text-xs font-medium text-foreground shadow-lg backdrop-blur transition-colors hover:border-primary/40 hover:bg-card"
        >
          <LifeBuoy className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          <span className="truncate">
            Keeps happening? Report it at{' '}
            <span className="font-semibold text-primary">
              1ani.me/help-center
            </span>
          </span>
        </a>
      </div>
    </div>
  );
}

(
  ErrorBoundary as unknown as { defaultProps: Partial<ErrorBoundaryProps> }
).defaultProps = {
  fallback: undefined,
};
